import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import ticketService, { 
  TICKET_STATUSES, 
  TICKET_CATEGORIES
} from '../services/ticketService';
import TicketListComponent from '../components/TicketListComponent';
import AlertModal from '../components/common/AlertModal';
import './TicketList.css';

const TicketList = () => {
  const { isAuthenticated } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    page: 1,
    page_size: 5  // 每页5条
  });
  const [totalCount, setTotalCount] = useState(0);
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  // 加载数据
  useEffect(() => {
    if (isAuthenticated) {
      loadTickets();
      loadStats();
    }
  }, [isAuthenticated, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // 轮询刷新工单列表状态（每30秒检查一次，仅在页面可见时）
  useEffect(() => {
    if (!isAuthenticated) return;

    // 使用 Page Visibility API 来检测页面是否可见
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 页面不可见时，停止轮询
        return;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const pollInterval = setInterval(() => {
      // 只在页面可见时进行轮询
      if (!document.hidden) {
        // 静默刷新工单列表和统计
        const params = {
          page: filters.page,
          page_size: filters.page_size,
          ...(filters.status && { status: filters.status }),
          ...(filters.category && { category: filters.category })
        };
        
        ticketService.getUserTickets(params)
          .then(response => {
            if (Array.isArray(response)) {
              setTickets(response);
              setTotalCount(response.length);
            } else {
              setTickets(response.tickets || []);
              setTotalCount(response.total || 0);
            }
          })
          .catch(err => {
            console.debug('Polling ticket list failed:', err);
          });

        ticketService.getTicketStats()
          .then(statsData => {
            setStats(statsData);
          })
          .catch(err => {
            console.debug('Polling ticket stats failed:', err);
          });
      }
    }, 30000); // 每30秒检查一次

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, filters]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: filters.page,
        page_size: filters.page_size,
        ...(filters.status && { status: filters.status }),
        ...(filters.category && { category: filters.category })
      };
      
      const response = await ticketService.getUserTickets(params);
      // 后端现在返回分页响应，包含 tickets 和 total
      if (Array.isArray(response)) {
        // 兼容旧格式（直接返回数组）
        setTickets(response);
        setTotalCount(response.length);
      } else {
        // 新格式（分页响应）
      setTickets(response.tickets || []);
      setTotalCount(response.total || 0);
      }
      
    } catch (err) {
      console.error('Error loading tickets:', err);
      setError(err.message || '加载工单失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await ticketService.getTicketStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
      // 统计加载失败不影响主要功能，只记录错误
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // 重置页码
    }));
  };

  // 分页处理函数
  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };



  // 表单处理函数
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // 清除对应字段的错误
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = '请输入工单标题';
    } else if (formData.title.trim().length < 5) {
      errors.title = '标题至少需要5个字符';
    }
    
    if (!formData.category) {
      errors.category = '请选择工单分类';
    }
    
    if (!formData.description.trim()) {
      errors.description = '请输入问题描述';
    } else if (formData.description.trim().length < 10) {
      errors.description = '描述至少需要10个字符';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await ticketService.createTicket(formData);
      
      // 提交成功后关闭弹框并重置表单
      setShowCreateForm(false);
      setFormData({
        title: '',
        category: '',
        description: ''
      });
      setFormErrors({});
      
      // 重新加载工单列表和统计
      await loadTickets();
      await loadStats();
      
      // 显示成功提示
      setAlertModal({
        isOpen: true,
        title: '提交成功',
        message: '工单提交成功！',
        type: 'success'
      });
      
    } catch (error) {
      console.error('Error submitting ticket:', error);
      setAlertModal({
        isOpen: true,
        title: '提交失败',
        message: error.message || '创建工单失败，请稍后重试',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
    setFormData({
      title: '',
      category: '',
      description: ''
    });
    setFormErrors({});
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingText>加载中...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>我的工单</PageTitle>
        <CreateButton onClick={() => setShowCreateForm(true)}>
          提交工单
        </CreateButton>
      </PageHeader>

      {/* 简化的筛选和统计 */}
      <ControlBar>
        <FiltersRow>
          <FilterSelect 
            value={filters.status} 
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
          <option value="">全部状态</option>
          <option value={TICKET_STATUSES.PENDING}>待处理</option>
          <option value={TICKET_STATUSES.IN_PROGRESS}>处理中</option>
          <option value={TICKET_STATUSES.RESOLVED}>已解决</option>
          </FilterSelect>

          <FilterSelect 
            value={filters.category} 
            onChange={(e) => handleFilterChange('category', e.target.value)}
          >
            <option value="">全部分类</option>
            <option value={TICKET_CATEGORIES.TECHNICAL}>技术问题</option>
            <option value={TICKET_CATEGORIES.ACCOUNT}>账户问题</option>
            <option value={TICKET_CATEGORIES.PAYMENT}>支付问题</option>
            <option value={TICKET_CATEGORIES.FEATURE_REQUEST}>功能建议</option>
            <option value={TICKET_CATEGORIES.BUG_REPORT}>问题反馈</option>
            <option value={TICKET_CATEGORIES.OTHER}>其他</option>
          </FilterSelect>
        </FiltersRow>

        {stats && (
          <StatsRow>
            <StatItem>总计 <StatNumber>{stats.total_tickets}</StatNumber></StatItem>
            <StatItem>待处理 <StatNumber highlight>{stats.pending_tickets}</StatNumber></StatItem>
            <StatItem>处理中 <StatNumber>{stats.in_progress_tickets}</StatNumber></StatItem>
            <StatItem>已解决 <StatNumber>{stats.resolved_tickets}</StatNumber></StatItem>
          </StatsRow>
        )}
      </ControlBar>

      {/* 工单列表 */}
      {error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : (
        <TicketListComponent
          tickets={tickets}
          totalCount={totalCount}
          currentPage={filters.page}
          pageSize={filters.page_size}
          onPageChange={handlePageChange}
        />
      )}

      {/* 工单创建弹框 */}
      {showCreateForm && (
        <ModalOverlay onClick={handleCloseForm}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>提交工单</ModalTitle>
              <CloseButton onClick={handleCloseForm}>×</CloseButton>
            </ModalHeader>
            
            <Form onSubmit={handleSubmit}>
              <FormRow>
                <FormGroup>
                  <Label htmlFor="title">工单标题 <RequiredMark>*</RequiredMark></Label>
                  <Input
                    id="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    placeholder="请简要描述您的问题"
                    error={formErrors.title}
                  />
                  {formErrors.title && <ErrorMessage>{formErrors.title}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="category">分类 <RequiredMark>*</RequiredMark></Label>
                  <Select
                    id="category"
                    value={formData.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                    error={formErrors.category}
                  >
                    <option value="">请选择分类</option>
                    <option value={TICKET_CATEGORIES.TECHNICAL}>技术问题</option>
                    <option value={TICKET_CATEGORIES.ACCOUNT}>账户问题</option>
                    <option value={TICKET_CATEGORIES.PAYMENT}>支付问题</option>
                    <option value={TICKET_CATEGORIES.FEATURE_REQUEST}>功能建议</option>
                    <option value={TICKET_CATEGORIES.BUG_REPORT}>问题反馈</option>
                    <option value={TICKET_CATEGORIES.OTHER}>其他</option>
                  </Select>
                  {formErrors.category && <ErrorMessage>{formErrors.category}</ErrorMessage>}
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label htmlFor="description">问题描述 <RequiredMark>*</RequiredMark></Label>
                <TextArea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  placeholder="请详细描述您遇到的问题，包括复现步骤、期望结果等"
                  rows={6}
                  error={formErrors.description}
                />
                {formErrors.description && <ErrorMessage>{formErrors.description}</ErrorMessage>}
              </FormGroup>

              <FormActions>
                <CancelButton type="button" onClick={handleCloseForm}>
                  取消
                </CancelButton>
                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? '提交中...' : '提交'}
                </SubmitButton>
              </FormActions>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* 统一错误提示弹框 */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
    </PageContainer>
  );
};

export default TicketList;

// 简化的样式组件
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 20px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
`;

const CreateButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// 控制栏：筛选器和统计
const ControlBar = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const FilterSelect = styled.select`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #f1f5f9;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #334155;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 40px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  &:hover {
    border-color: #cbd5e0;
    background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: none;
    border-color: #667eea;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.12), 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  option {
    padding: 8px 12px;
    font-weight: 500;
    color: #334155;
    background: white;
  }
  
  option:hover {
    background: #f1f5f9;
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 24px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  
  @media (max-width: 768px) {
    gap: 12px;
    flex-wrap: wrap;
  }
`;

const StatItem = styled.div`
  font-size: 14px;
  color: #718096;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatNumber = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: ${props => props.highlight ? '#f56565' : '#2d3748'};
`;

// 加载和错误状态
const LoadingContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const LoadingText = styled.div`
  color: #718096;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  background: #fff5f5;
  color: #c53030;
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid #feb2b2;
  font-size: 14px;
  margin-bottom: 20px;
`;

// 弹框样式
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.3s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    color: #334155;
  }
`;

// 表单样式
const Form = styled.form`
  padding: 0 24px 24px 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const RequiredMark = styled.span`
  color: #ef4444;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.error ? '#ef4444' : '#e5e7eb'};
  border-radius: 10px;
  font-size: 14px;
  color: #374151;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.error ? '#ef4444' : '#e5e7eb'};
  border-radius: 10px;
  font-size: 14px;
  color: #374151;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 40px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.error ? '#ef4444' : '#e5e7eb'};
  border-radius: 10px;
  font-size: 14px;
  color: #374151;
  background: white;
  resize: vertical;
  min-height: 120px;
  font-family: inherit;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: white;
  color: #64748b;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #374151;
  }
`;

const SubmitButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;
